// @flow
import {fabric} from 'fabric';

import type {BiePluginType} from '../types';
import {CanvasManager} from '../managers/canvas-manager';

export class BieArrowPlugin implements BiePluginType {
  cm: CanvasManager;

  mode = 'waitForClick';
  tempStartPoint = {x: -1, y: -1};
  currentlyDrawnLine: fabric.Line;
  currentlyDrawnTriangle: fabric.Triangle;

  objectsCount: number;

  currentColor: string;

  setup() {}

  getActionButtons() {
    return [
      {
        type: 'enter-state',
        title: 'Draw Arrow',
        icon: 'fas fa-long-arrow-alt-up',
        slug: 'arrow',
        component: 'div',
        enter: this.start,
        cancel: this.cancel,
        confirm: this.confirm,
        triggerEvent: this.handleEvent,
        eventButtons: [
          {
            type: 'colorPalette',
          },
        ],
      },
    ];
  }

  removeListeners(cm: CanvasManager) {
    cm.canvas.off('mouse:down', this.onMouseDown);
    cm.canvas.off('mouse:up', this.onMouseUp);
    cm.canvas.off('mouse:move', this.onMouseMove);

    cm.canvas.selection = true;

    cm.canvas.hoverCursor = 'move';
  }

  handleEvent = (cm: CanvasManager, event: any) => {
    this.currentColor = event.type;
  };

  cancel = (cm: CanvasManager) => {
    this.removeListeners(cm);
    cm.canvas
      .getObjects()
      .slice(this.objectsCount)
      .map((object) => cm.canvas.remove(object));
    cm.canvas.renderAll();
  };

  confirm = (cm: CanvasManager) => {
    this.removeListeners(cm);
    cm.saveState();
  };

  start = (cm: CanvasManager) => {
    this.currentColor = 'red';
    this.cm = cm;
    this.objectsCount = cm.canvas.getObjects().length;

    cm.canvas.on('mouse:down', this.onMouseDown);
    cm.canvas.on('mouse:up', this.onMouseUp);
    cm.canvas.on('mouse:move', this.onMouseMove);

    cm.canvas.selection = false;
    cm.canvas.hoverCursor = 'crosshair';
  };

  onMouseDown = (event) => {
    if (this.cm.canvas.getActiveObject() != null) {
      return;
    }

    this.mode = 'tempDrawLine';

    this.tempStartPoint.x = event.absolutePointer.x;
    this.tempStartPoint.y = event.absolutePointer.y;
  };

  onMouseUp = (event) => {
    this.mode = 'waitForClick';
    if (
      this.currentlyDrawnTriangle != null &&
      this.currentlyDrawnLine != null
    ) {
      let xDiffLine = this.currentlyDrawnLine.x1 - this.currentlyDrawnLine.x2;
      let yDiffLine = this.currentlyDrawnLine.y1 - this.currentlyDrawnLine.y2;

      if (xDiffLine > 0 && yDiffLine <= 0) {
        // Bottom left
        yDiffLine =
          this.currentlyDrawnLine.y1 - this.currentlyDrawnTriangle.top;
      } else if (xDiffLine <= 0 && yDiffLine > 0) {
        // Top right
        xDiffLine =
          -(this.currentlyDrawnLine.y1 - this.currentlyDrawnTriangle.top) - 10;
      } else if (xDiffLine <= 0 && yDiffLine <= 0) {
        //Bottom right
        yDiffLine =
          this.currentlyDrawnLine.x1 - this.currentlyDrawnTriangle.left;
        xDiffLine =
          this.currentlyDrawnLine.x1 - this.currentlyDrawnTriangle.left;
      }

      var group = new fabric.Group(
        [this.currentlyDrawnTriangle, this.currentlyDrawnLine],
        {
          left: this.tempStartPoint.x - xDiffLine,
          top: this.tempStartPoint.y - yDiffLine,
        }
      );
      this.cm.canvas.remove(this.currentlyDrawnTriangle);
      this.cm.canvas.remove(this.currentlyDrawnLine);
      this.cm.canvas.add(group);
      this.cm.canvas.renderAll();
    }

    this.currentlyDrawnLine = null;
    this.currentlyDrawnTriangle = null;
  };

  onMouseMove = (event) => {
    if (this.mode === 'tempDrawLine') {
      if (this.currentlyDrawnLine == null) {
        this.currentlyDrawnTriangle = new fabric.Triangle({
          width: 76,
          height: 76,
          fill: this.currentColor,
          left: this.tempStartPoint.x - 76 / 2 - 0.5 + 5,
          top: this.tempStartPoint.y - 76 / 2 - 0.5 + 5,
          angle: 0,
        });

        this.currentlyDrawnLine = new fabric.Line(
          [
            this.tempStartPoint.x,
            this.tempStartPoint.y,
            event.absolutePointer.x,
            event.absolutePointer.y,
          ],
          {
            strokeWidth: 10,
            stroke: this.currentColor,
          }
        );

        this.cm.canvas.add(this.currentlyDrawnLine);
        this.cm.canvas.add(this.currentlyDrawnTriangle);
      } else {
        const pointer = event.absolutePointer;

        const xDiff = this.tempStartPoint.x - pointer.x;
        const yDiff = this.tempStartPoint.y - pointer.y;
        const tgAngle = yDiff / xDiff;
        let angle = (Math.atan(tgAngle) * 180) / Math.PI;

        angle = Math.round(angle / 10) * 10;

        if (Math.abs(xDiff) >= Math.abs(yDiff)) {
          const newYDiff = Math.tan((angle / 180) * Math.PI) * xDiff;
          pointer.y = this.tempStartPoint.y - newYDiff;
        } else {
          const newXDiff = yDiff / Math.tan((angle / 180) * Math.PI);
          pointer.x = this.tempStartPoint.x - newXDiff;
        }

        if (xDiff < 0) {
          angle += 180;
        }

        this.currentlyDrawnLine.set({
          x2: pointer.x,
          y2: pointer.y,
        });

        this.currentlyDrawnTriangle.rotate(angle + 90);
        this.cm.canvas.renderAll();
      }
    }
  };
}
